export interface PagePermission {
    edit: string;
    read: string;
    create: string;
    delete: string;
    restore: string;
    stream: string;
    enabled: boolean;
    logs: string;
    export: string;
    request: string;
    approve: string;
    invite: string;
    dashboard: string;
    report: string;
    finance: string;
    [key: string]: string | boolean;
}


export interface Page {
    name: string;
    permissions: PagePermission;
}

interface FieldPermission {
    edit: string;
    read: string;
}

export interface Field {
    entity: string;
    fields: Record<string, FieldPermission>;
}

export enum PermissionLevel {
    Yes = 'yes',
    Team = 'team',
    Own = 'own',
    Office = 'office',
    No = 'no',
    All = 'all',
    Enabled = 'enabled',
    Disabled = 'disabled',
    NotSet = 'not-set',
}
export enum PermissionAction {
    EDIT= 'edit',
    READ= 'read',
    CREATE= 'create',
    DELETE= 'delete',
    RESTORE= 'restore',
    STREAM= 'stream',
    LOGS= 'logs',
    EXPORT= 'export',
    REQUEST= 'request',
    APPROVE= 'approve',
    DASHBOARD= 'dashboard',
    REPORT= 'report',
    INVITE= 'invite',
    FINANCE= 'finance',
}
export enum PageName {
    LOGS = 'Logs',
    CALLS = 'Calls',
    LEADS = 'Leads',
    ROLES = 'Roles',
    TASKS = 'Tasks',
    TEAMS = 'Teams',
    USERS = 'Users',
    CLIENTS = 'Clients',
    CONFIGS = 'Configs',
    CONTACTS = 'Contacts',
    EXPENSES = 'Expenses',
    INCIDENTS = 'Incidents',
    INVOICES = 'Invoices',
    MEETINGS = 'Meetings',
    MISSIONS = 'Missions',
    PROVIDERS = 'Providers',
    COMPANIES='Companies',
    ACTIVITIES = 'Activities',
    ORGANIZATION = 'Organization',
    MISSION_REQUESTS = 'Mission requests',
    DASHBOARD = 'Dashboard',
    STREAM = 'Stream',
    LOOKUPS = 'Lookups',
    SERVICES = 'Services',
    REGIONS = 'Regions',
    COUNTRIES = 'Countries',
    STATES = 'States',
    CITIES = 'Cities',
    TIMEZONES = 'TimeZones',
    AIRPORTS = 'Airports',
    MANUFACTURERS = 'Manufacturers',
    LANGUAGES= 'Languages',
    COLORS = 'Colors',
    TAGS = 'Tags',
    JOB_POSITIONS='Job Positions',
    SPECIALTY='Specialty',
    CONTINENT='Continent',
    BILLS = 'Bills',
    PAYMENTS = 'Payments',
    CREDIT_NOTES = 'Credit notes',
    JOURNAL_ENTRIES = 'Journal entries',
    FINANCIAL_REPORTS = 'Financial reports',
    CHART_OF_ACCOUNTS = 'Chart of accounts',
    FISCAL_PERIODS = 'Fiscal periods',
    TASK_TEMPLATES = 'Task templates',
    ORGANIZATION_RATINGS = 'Organization ratings',
    RATING_DIMENSIONS = 'Rating dimensions',
    INCIDENT_REPORTS = 'Incident reports',
    NOTIFICATIONS = 'Notifications',
    LIVE_POSITION = 'Live position',
    PANIC = 'Panic',
    MOBILE_DEVICE = 'Mobile device',
    EMPLOYEES = 'Employees',
    HR_SETTINGS = 'HR settings',
    HR_CASES = 'HR cases',
    LIFECYCLE = 'Lifecycle',
    PAYROLL = 'Payroll',
    PURCHASES = 'Purchases',
    EXPENSE_CLAIMS = 'Expense claims',
    ATTENDANCE = 'Attendance',
    LEAVE = 'Leave',
    ROTA = 'Rota',
    AI_SETTINGS = 'AI settings',
    // Business development
    BUSINESS_DEVELOPMENT = 'Business development',
    BD_SETTINGS = 'BD settings',
    // Research: each person sees only their own pages, whatever the level
    RESEARCH = 'Research',
    // Marketing
    MARKETING = 'Marketing',
    MARKETING_REVIEW = 'Marketing review',
    MARKETING_SETTINGS = 'Marketing settings',
    // Missions: the follow-up rules, stage checks, ops room shifts and reminders
    MISSION_SETTINGS = 'Mission settings',
    // Training
    TRAINING = 'Training',
    TRAINING_SETTINGS = 'Training settings',
}
export const PagesPermissions ={
    LOGS_READ : `${PageName.LOGS}:${PermissionAction.READ}`,
    LOGS_CREATE : `${PageName.LOGS}:${PermissionAction.CREATE}`,
    LOGS_EDIT : `${PageName.LOGS}:${PermissionAction.EDIT}`,
    LOGS_DELETE : `${PageName.LOGS}:${PermissionAction.DELETE}`,
    LOGS_RESTORE : `${PageName.LOGS}:${PermissionAction.RESTORE}`,

    CALLS_READ : `${PageName.CALLS}:${PermissionAction.READ}`,
    CALLS_CREATE : `${PageName.CALLS}:${PermissionAction.CREATE}`,
    CALLS_EDIT : `${PageName.CALLS}:${PermissionAction.EDIT}`,
    CALLS_DELETE : `${PageName.CALLS}:${PermissionAction.DELETE}`,
    CALLS_RESTORE : `${PageName.CALLS}:${PermissionAction.RESTORE}`,

    LEADS_DASHBOARD : `${PageName.LEADS}:${PermissionAction.DASHBOARD}`,
    LEADS_REPORT : `${PageName.LEADS}:${PermissionAction.REPORT}`,
    LEADS_READ : `${PageName.LEADS}:${PermissionAction.READ}`,
    LEADS_CREATE : `${PageName.LEADS}:${PermissionAction.CREATE}`,
    LEADS_EDIT : `${PageName.LEADS}:${PermissionAction.EDIT}`,
    LEADS_DELETE : `${PageName.LEADS}:${PermissionAction.DELETE}`,
    LEADS_RESTORE : `${PageName.LEADS}:${PermissionAction.RESTORE}`,

    ROLES_READ : `${PageName.ROLES}:${PermissionAction.READ}`,
    ROLES_CREATE : `${PageName.ROLES}:${PermissionAction.CREATE}`,
    ROLES_EDIT : `${PageName.ROLES}:${PermissionAction.EDIT}`,
    ROLES_DELETE : `${PageName.ROLES}:${PermissionAction.DELETE}`,
    ROLES_RESTORE : `${PageName.ROLES}:${PermissionAction.RESTORE}`,

    TASKS_DASHBOARD : `${PageName.TASKS}:${PermissionAction.DASHBOARD}`,
    TASKS_READ : `${PageName.TASKS}:${PermissionAction.READ}`,
    TASKS_CREATE : `${PageName.TASKS}:${PermissionAction.CREATE}`,
    TASKS_EDIT : `${PageName.TASKS}:${PermissionAction.EDIT}`,
    TASKS_DELETE : `${PageName.TASKS}:${PermissionAction.DELETE}`,
    TASKS_RESTORE : `${PageName.TASKS}:${PermissionAction.RESTORE}`,

    TASK_TEMPLATES_READ : `${PageName.TASK_TEMPLATES}:${PermissionAction.READ}`,
    TASK_TEMPLATES_CREATE : `${PageName.TASK_TEMPLATES}:${PermissionAction.CREATE}`,
    TASK_TEMPLATES_EDIT : `${PageName.TASK_TEMPLATES}:${PermissionAction.EDIT}`,
    TASK_TEMPLATES_DELETE : `${PageName.TASK_TEMPLATES}:${PermissionAction.DELETE}`,
    TASK_TEMPLATES_RESTORE : `${PageName.TASK_TEMPLATES}:${PermissionAction.RESTORE}`,

    ORGANIZATION_RATINGS_READ : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.READ}`,
    ORGANIZATION_RATINGS_CREATE : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.CREATE}`,
    ORGANIZATION_RATINGS_EDIT : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.EDIT}`,
    ORGANIZATION_RATINGS_DELETE : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.DELETE}`,
    ORGANIZATION_RATINGS_RESTORE : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.RESTORE}`,
    ORGANIZATION_RATINGS_DASHBOARD : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.DASHBOARD}`,
    ORGANIZATION_RATINGS_REPORT : `${PageName.ORGANIZATION_RATINGS}:${PermissionAction.REPORT}`,

    RATING_DIMENSIONS_READ : `${PageName.RATING_DIMENSIONS}:${PermissionAction.READ}`,
    RATING_DIMENSIONS_CREATE : `${PageName.RATING_DIMENSIONS}:${PermissionAction.CREATE}`,
    RATING_DIMENSIONS_EDIT : `${PageName.RATING_DIMENSIONS}:${PermissionAction.EDIT}`,
    RATING_DIMENSIONS_DELETE : `${PageName.RATING_DIMENSIONS}:${PermissionAction.DELETE}`,
    RATING_DIMENSIONS_RESTORE : `${PageName.RATING_DIMENSIONS}:${PermissionAction.RESTORE}`,

    TEAMS_READ : `${PageName.TEAMS}:${PermissionAction.READ}`,
    TEAMS_CREATE : `${PageName.TEAMS}:${PermissionAction.CREATE}`,
    TEAMS_EDIT : `${PageName.TEAMS}:${PermissionAction.EDIT}`,
    TEAMS_DELETE : `${PageName.TEAMS}:${PermissionAction.DELETE}`,
    TEAMS_RESTORE : `${PageName.TEAMS}:${PermissionAction.RESTORE}`,

    USERS_READ : `${PageName.USERS}:${PermissionAction.READ}`,
    USERS_CREATE : `${PageName.USERS}:${PermissionAction.CREATE}`,
    USERS_EDIT : `${PageName.USERS}:${PermissionAction.EDIT}`,
    USERS_DELETE : `${PageName.USERS}:${PermissionAction.DELETE}`,
    USERS_RESTORE : `${PageName.USERS}:${PermissionAction.RESTORE}`,
    USERS_INVITE : `${PageName.USERS}:${PermissionAction.INVITE}`,

    CLIENTS_DASHBOARD : `${PageName.CLIENTS}:${PermissionAction.DASHBOARD}`,
    CLIENTS_REPORT : `${PageName.CLIENTS}:${PermissionAction.REPORT}`,
    CLIENTS_READ : `${PageName.CLIENTS}:${PermissionAction.READ}`,
    CLIENTS_CREATE : `${PageName.CLIENTS}:${PermissionAction.CREATE}`,
    CLIENTS_EDIT : `${PageName.CLIENTS}:${PermissionAction.EDIT}`,
    CLIENTS_DELETE : `${PageName.CLIENTS}:${PermissionAction.DELETE}`,
    CLIENTS_RESTORE : `${PageName.CLIENTS}:${PermissionAction.RESTORE}`,

    CONFIGS_READ : `${PageName.CONFIGS}:${PermissionAction.READ}`,
    CONFIGS_CREATE : `${PageName.CONFIGS}:${PermissionAction.CREATE}`,
    CONFIGS_EDIT : `${PageName.CONFIGS}:${PermissionAction.EDIT}`,
    CONFIGS_DELETE : `${PageName.CONFIGS}:${PermissionAction.DELETE}`,
    CONFIGS_RESTORE : `${PageName.CONFIGS}:${PermissionAction.RESTORE}`,

    lOOKUPS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    lOOKUPS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    lOOKUPS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    lOOKUPS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    lOOKUPS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    CONTACTS_DASHBOARD : `${PageName.CONTACTS}:${PermissionAction.DASHBOARD}`,
    CONTACTS_REPORT : `${PageName.CONTACTS}:${PermissionAction.REPORT}`,
    CONTACTS_READ : `${PageName.CONTACTS}:${PermissionAction.READ}`,
    CONTACTS_CREATE : `${PageName.CONTACTS}:${PermissionAction.CREATE}`,
    CONTACTS_EDIT : `${PageName.CONTACTS}:${PermissionAction.EDIT}`,
    CONTACTS_DELETE : `${PageName.CONTACTS}:${PermissionAction.DELETE}`,
    CONTACTS_RESTORE : `${PageName.CONTACTS}:${PermissionAction.RESTORE}`,

    INVOICES_READ : `${PageName.INVOICES}:${PermissionAction.READ}`,
    INVOICES_CREATE : `${PageName.INVOICES}:${PermissionAction.CREATE}`,
    INVOICES_EDIT : `${PageName.INVOICES}:${PermissionAction.EDIT}`,
    INVOICES_DELETE : `${PageName.INVOICES}:${PermissionAction.DELETE}`,
    INVOICES_RESTORE : `${PageName.INVOICES}:${PermissionAction.RESTORE}`,
    INVOICES_LOGS : `${PageName.INVOICES}:${PermissionAction.LOGS}`,

    MEETINGS_READ : `${PageName.MEETINGS}:${PermissionAction.READ}`,
    MEETINGS_CREATE : `${PageName.MEETINGS}:${PermissionAction.CREATE}`,
    MEETINGS_EDIT : `${PageName.MEETINGS}:${PermissionAction.EDIT}`,
    MEETINGS_DELETE : `${PageName.MEETINGS}:${PermissionAction.DELETE}`,
    MEETINGS_RESTORE : `${PageName.MEETINGS}:${PermissionAction.RESTORE}`,

    MISSIONS_READ : `${PageName.MISSIONS}:${PermissionAction.READ}`,
    MISSIONS_CREATE : `${PageName.MISSIONS}:${PermissionAction.CREATE}`,
    MISSIONS_EDIT : `${PageName.MISSIONS}:${PermissionAction.EDIT}`,
    MISSIONS_DELETE : `${PageName.MISSIONS}:${PermissionAction.DELETE}`,
    MISSIONS_RESTORE : `${PageName.MISSIONS}:${PermissionAction.RESTORE}`,
    MISSIONS_REQUEST : `${PageName.MISSIONS}:${PermissionAction.REQUEST}`,
    MISSIONS_APPROVE : `${PageName.MISSIONS}:${PermissionAction.APPROVE}`,
    MISSIONS_DASHBOARD : `${PageName.MISSIONS}:${PermissionAction.DASHBOARD}`,
    MISSIONS_REPORT : `${PageName.MISSIONS}:${PermissionAction.REPORT}`,
    MISSIONS_FINANCE : `${PageName.MISSIONS}:${PermissionAction.FINANCE}`,

    MISSION_REQUESTS_DASHBOARD : `${PageName.MISSION_REQUESTS}:${PermissionAction.DASHBOARD}`,
    MISSION_REQUESTS_REPORT : `${PageName.MISSION_REQUESTS}:${PermissionAction.REPORT}`,

    MISSION_REQUESTS_READ : `${PageName.MISSION_REQUESTS}:${PermissionAction.READ}`,
    MISSION_REQUESTS_CREATE : `${PageName.MISSION_REQUESTS}:${PermissionAction.CREATE}`,
    MISSION_REQUESTS_EDIT : `${PageName.MISSION_REQUESTS}:${PermissionAction.EDIT}`,
    MISSION_REQUESTS_DELETE : `${PageName.MISSION_REQUESTS}:${PermissionAction.DELETE}`,
    MISSION_REQUESTS_RESTORE : `${PageName.MISSION_REQUESTS}:${PermissionAction.RESTORE}`,
    MISSION_REQUESTS_REQUEST : `${PageName.MISSION_REQUESTS}:${PermissionAction.REQUEST}`,
    MISSION_REQUESTS_APPROVE : `${PageName.MISSION_REQUESTS}:${PermissionAction.APPROVE}`,
    MISSION_REQUESTS_FINANCE : `${PageName.MISSION_REQUESTS}:${PermissionAction.FINANCE}`,

    PROVIDERS_DASHBOARD : `${PageName.PROVIDERS}:${PermissionAction.DASHBOARD}`,
    PROVIDERS_REPORT : `${PageName.PROVIDERS}:${PermissionAction.REPORT}`,
    PROVIDERS_READ : `${PageName.PROVIDERS}:${PermissionAction.READ}`,
    PROVIDERS_CREATE : `${PageName.PROVIDERS}:${PermissionAction.CREATE}`,
    PROVIDERS_EDIT : `${PageName.PROVIDERS}:${PermissionAction.EDIT}`,
    PROVIDERS_DELETE : `${PageName.PROVIDERS}:${PermissionAction.DELETE}`,
    PROVIDERS_RESTORE : `${PageName.PROVIDERS}:${PermissionAction.RESTORE}`,
    PROVIDERS_FINANCE : `${PageName.PROVIDERS}:${PermissionAction.FINANCE}`,

    COMPANIES_DASHBOARD : `${PageName.COMPANIES}:${PermissionAction.DASHBOARD}`,
    COMPANIES_REPORT : `${PageName.COMPANIES}:${PermissionAction.REPORT}`,
    COMPANIES_READ : `${PageName.COMPANIES}:${PermissionAction.READ}`,
    COMPANIES_CREATE : `${PageName.COMPANIES}:${PermissionAction.CREATE}`,
    COMPANIES_EDIT : `${PageName.COMPANIES}:${PermissionAction.EDIT}`,
    COMPANIES_DELETE : `${PageName.COMPANIES}:${PermissionAction.DELETE}`,
    COMPANIES_RESTORE : `${PageName.COMPANIES}:${PermissionAction.RESTORE}`,

    ACTIVITIES_READ : `${PageName.ACTIVITIES}:${PermissionAction.READ}`,
    ACTIVITIES_CREATE : `${PageName.ACTIVITIES}:${PermissionAction.CREATE}`,
    ACTIVITIES_EDIT : `${PageName.ACTIVITIES}:${PermissionAction.EDIT}`,
    ACTIVITIES_DELETE : `${PageName.ACTIVITIES}:${PermissionAction.DELETE}`,
    ACTIVITIES_RESTORE : `${PageName.ACTIVITIES}:${PermissionAction.RESTORE}`,

    ORGANIZATION_READ : `${PageName.ORGANIZATION}:${PermissionAction.READ}`,
    ORGANIZATION_CREATE : `${PageName.ORGANIZATION}:${PermissionAction.CREATE}`,
    ORGANIZATION_EDIT : `${PageName.ORGANIZATION}:${PermissionAction.EDIT}`,
    ORGANIZATION_DELETE : `${PageName.ORGANIZATION}:${PermissionAction.DELETE}`,
    ORGANIZATION_RESTORE : `${PageName.ORGANIZATION}:${PermissionAction.RESTORE}`,

    DASHBOARD_REPORT : `${PageName.DASHBOARD}:${PermissionAction.REPORT}`,
    DASHBOARD_READ : `${PageName.DASHBOARD}:${PermissionAction.READ}`,
    DASHBOARD_CREATE : `${PageName.DASHBOARD}:${PermissionAction.CREATE}`,
    DASHBOARD_EDIT : `${PageName.DASHBOARD}:${PermissionAction.EDIT}`,
    DASHBOARD_DELETE : `${PageName.DASHBOARD}:${PermissionAction.DELETE}`,
    DASHBOARD_RESTORE : `${PageName.DASHBOARD}:${PermissionAction.RESTORE}`,

    STREAM_READ : `${PageName.STREAM}:${PermissionAction.READ}`,
    STREAM_CREATE : `${PageName.STREAM}:${PermissionAction.CREATE}`,
    STREAM_EDIT : `${PageName.STREAM}:${PermissionAction.EDIT}`,
    STREAM_DELETE : `${PageName.STREAM}:${PermissionAction.DELETE}`,
    STREAM_RESTORE : `${PageName.STREAM}:${PermissionAction.RESTORE}`,


    // LOOKUPS REGION

    EXPENSES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    EXPENSES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    EXPENSES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    EXPENSES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    EXPENSES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    INCIDENTS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    INCIDENTS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    INCIDENTS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    INCIDENTS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    INCIDENTS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    SERVICES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    SERVICES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    SERVICES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    SERVICES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    SERVICES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    REGIONS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    REGIONS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    REGIONS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    REGIONS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    REGIONS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    CONTINENT_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    CONTINENT_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    CONTINENT_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    CONTINENT_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    CONTINENT_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    COUNTRIES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    COUNTRIES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    COUNTRIES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    COUNTRIES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    COUNTRIES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    STATES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    STATES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    STATES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    STATES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    STATES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    CITIES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    CITIES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    CITIES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    CITIES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    CITIES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    AIRPORTS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    AIRPORTS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    AIRPORTS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    AIRPORTS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    AIRPORTS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    MANUFACTURERS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    MANUFACTURERS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    MANUFACTURERS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    MANUFACTURERS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    MANUFACTURERS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    LANGUAGES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    LANGUAGES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    LANGUAGES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    LANGUAGES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    LANGUAGES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    COLORS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    COLORS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    COLORS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    COLORS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    COLORS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    TIMEZONES_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    TIMEZONES_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    TIMEZONES_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    TIMEZONES_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    TIMEZONES_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    TAGS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    TAGS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    TAGS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    TAGS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    TAGS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    JOB_POSITIONS_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    JOB_POSITIONS_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    JOB_POSITIONS_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    JOB_POSITIONS_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    JOB_POSITIONS_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    SPECIALTY_READ : `${PageName.LOOKUPS}:${PermissionAction.READ}`,
    SPECIALTY_CREATE : `${PageName.LOOKUPS}:${PermissionAction.CREATE}`,
    SPECIALTY_EDIT : `${PageName.LOOKUPS}:${PermissionAction.EDIT}`,
    SPECIALTY_DELETE : `${PageName.LOOKUPS}:${PermissionAction.DELETE}`,
    SPECIALTY_RESTORE : `${PageName.LOOKUPS}:${PermissionAction.RESTORE}`,

    INVOICES_APPROVE : `${PageName.INVOICES}:${PermissionAction.APPROVE}`,
    INVOICES_REPORT : `${PageName.INVOICES}:${PermissionAction.REPORT}`,
    BILLS_REPORT : `${PageName.BILLS}:${PermissionAction.REPORT}`,
    PAYMENTS_REPORT : `${PageName.PAYMENTS}:${PermissionAction.REPORT}`,
    CREDIT_NOTES_REPORT : `${PageName.CREDIT_NOTES}:${PermissionAction.REPORT}`,
    JOURNAL_ENTRIES_REPORT : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.REPORT}`,
    FINANCIAL_REPORTS_REPORT : `${PageName.FINANCIAL_REPORTS}:${PermissionAction.REPORT}`,
    CHART_OF_ACCOUNTS_REPORT : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.REPORT}`,
    FISCAL_PERIODS_REPORT : `${PageName.FISCAL_PERIODS}:${PermissionAction.REPORT}`,

    BILLS_READ : `${PageName.BILLS}:${PermissionAction.READ}`,
    BILLS_CREATE : `${PageName.BILLS}:${PermissionAction.CREATE}`,
    BILLS_EDIT : `${PageName.BILLS}:${PermissionAction.EDIT}`,
    BILLS_DELETE : `${PageName.BILLS}:${PermissionAction.DELETE}`,
    BILLS_RESTORE : `${PageName.BILLS}:${PermissionAction.RESTORE}`,
    BILLS_LOGS : `${PageName.BILLS}:${PermissionAction.LOGS}`,
    BILLS_APPROVE : `${PageName.BILLS}:${PermissionAction.APPROVE}`,

    PAYMENTS_READ : `${PageName.PAYMENTS}:${PermissionAction.READ}`,
    PAYMENTS_CREATE : `${PageName.PAYMENTS}:${PermissionAction.CREATE}`,
    PAYMENTS_EDIT : `${PageName.PAYMENTS}:${PermissionAction.EDIT}`,
    PAYMENTS_DELETE : `${PageName.PAYMENTS}:${PermissionAction.DELETE}`,
    PAYMENTS_RESTORE : `${PageName.PAYMENTS}:${PermissionAction.RESTORE}`,
    PAYMENTS_LOGS : `${PageName.PAYMENTS}:${PermissionAction.LOGS}`,
    PAYMENTS_APPROVE : `${PageName.PAYMENTS}:${PermissionAction.APPROVE}`,

    CREDIT_NOTES_READ : `${PageName.CREDIT_NOTES}:${PermissionAction.READ}`,
    CREDIT_NOTES_CREATE : `${PageName.CREDIT_NOTES}:${PermissionAction.CREATE}`,
    CREDIT_NOTES_EDIT : `${PageName.CREDIT_NOTES}:${PermissionAction.EDIT}`,
    CREDIT_NOTES_DELETE : `${PageName.CREDIT_NOTES}:${PermissionAction.DELETE}`,
    CREDIT_NOTES_RESTORE : `${PageName.CREDIT_NOTES}:${PermissionAction.RESTORE}`,
    CREDIT_NOTES_APPROVE : `${PageName.CREDIT_NOTES}:${PermissionAction.APPROVE}`,

    JOURNAL_ENTRIES_READ : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.READ}`,
    JOURNAL_ENTRIES_CREATE : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.CREATE}`,
    JOURNAL_ENTRIES_EDIT : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.EDIT}`,
    JOURNAL_ENTRIES_DELETE : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.DELETE}`,
    JOURNAL_ENTRIES_RESTORE : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.RESTORE}`,
    JOURNAL_ENTRIES_APPROVE : `${PageName.JOURNAL_ENTRIES}:${PermissionAction.APPROVE}`,

    FINANCIAL_REPORTS_READ : `${PageName.FINANCIAL_REPORTS}:${PermissionAction.READ}`,
    FINANCIAL_REPORTS_EXPORT : `${PageName.FINANCIAL_REPORTS}:${PermissionAction.EXPORT}`,

    CHART_OF_ACCOUNTS_READ : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.READ}`,
    CHART_OF_ACCOUNTS_CREATE : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.CREATE}`,
    CHART_OF_ACCOUNTS_EDIT : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.EDIT}`,
    CHART_OF_ACCOUNTS_DELETE : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.DELETE}`,
    CHART_OF_ACCOUNTS_LOGS : `${PageName.CHART_OF_ACCOUNTS}:${PermissionAction.LOGS}`,

    FISCAL_PERIODS_READ : `${PageName.FISCAL_PERIODS}:${PermissionAction.READ}`,
    FISCAL_PERIODS_CREATE : `${PageName.FISCAL_PERIODS}:${PermissionAction.CREATE}`,
    FISCAL_PERIODS_EDIT : `${PageName.FISCAL_PERIODS}:${PermissionAction.EDIT}`,
    FISCAL_PERIODS_DELETE : `${PageName.FISCAL_PERIODS}:${PermissionAction.DELETE}`,
    FISCAL_PERIODS_LOGS : `${PageName.FISCAL_PERIODS}:${PermissionAction.LOGS}`,
    FISCAL_PERIODS_APPROVE : `${PageName.FISCAL_PERIODS}:${PermissionAction.APPROVE}`,

    INCIDENT_REPORTS_READ : `${PageName.INCIDENT_REPORTS}:${PermissionAction.READ}`,
    INCIDENT_REPORTS_CREATE : `${PageName.INCIDENT_REPORTS}:${PermissionAction.CREATE}`,
    INCIDENT_REPORTS_EDIT : `${PageName.INCIDENT_REPORTS}:${PermissionAction.EDIT}`,
    INCIDENT_REPORTS_DELETE : `${PageName.INCIDENT_REPORTS}:${PermissionAction.DELETE}`,
    INCIDENT_REPORTS_RESTORE : `${PageName.INCIDENT_REPORTS}:${PermissionAction.RESTORE}`,
    INCIDENT_REPORTS_LOGS : `${PageName.INCIDENT_REPORTS}:${PermissionAction.LOGS}`,
    INCIDENT_REPORTS_EXPORT : `${PageName.INCIDENT_REPORTS}:${PermissionAction.EXPORT}`,

    NOTIFICATIONS_READ : `${PageName.NOTIFICATIONS}:${PermissionAction.READ}`,
    NOTIFICATIONS_CREATE : `${PageName.NOTIFICATIONS}:${PermissionAction.CREATE}`,
    NOTIFICATIONS_EDIT : `${PageName.NOTIFICATIONS}:${PermissionAction.EDIT}`,
    NOTIFICATIONS_DELETE : `${PageName.NOTIFICATIONS}:${PermissionAction.DELETE}`,
    NOTIFICATIONS_LOGS : `${PageName.NOTIFICATIONS}:${PermissionAction.LOGS}`,

    LIVE_POSITION_READ : `${PageName.LIVE_POSITION}:${PermissionAction.READ}`,
    LIVE_POSITION_CREATE : `${PageName.LIVE_POSITION}:${PermissionAction.CREATE}`,
    LIVE_POSITION_LOGS : `${PageName.LIVE_POSITION}:${PermissionAction.LOGS}`,

    PANIC_READ : `${PageName.PANIC}:${PermissionAction.READ}`,
    PANIC_CREATE : `${PageName.PANIC}:${PermissionAction.CREATE}`,
    PANIC_LOGS : `${PageName.PANIC}:${PermissionAction.LOGS}`,

    MOBILE_DEVICE_READ : `${PageName.MOBILE_DEVICE}:${PermissionAction.READ}`,
    MOBILE_DEVICE_CREATE : `${PageName.MOBILE_DEVICE}:${PermissionAction.CREATE}`,
    MOBILE_DEVICE_EDIT : `${PageName.MOBILE_DEVICE}:${PermissionAction.EDIT}`,
    MOBILE_DEVICE_DELETE : `${PageName.MOBILE_DEVICE}:${PermissionAction.DELETE}`,
    MOBILE_DEVICE_LOGS : `${PageName.MOBILE_DEVICE}:${PermissionAction.LOGS}`,

    EMPLOYEES_READ : `${PageName.EMPLOYEES}:${PermissionAction.READ}`,
    EMPLOYEES_CREATE : `${PageName.EMPLOYEES}:${PermissionAction.CREATE}`,
    EMPLOYEES_EDIT : `${PageName.EMPLOYEES}:${PermissionAction.EDIT}`,
    EMPLOYEES_DELETE : `${PageName.EMPLOYEES}:${PermissionAction.DELETE}`,
    EMPLOYEES_RESTORE : `${PageName.EMPLOYEES}:${PermissionAction.RESTORE}`,
    EMPLOYEES_LOGS : `${PageName.EMPLOYEES}:${PermissionAction.LOGS}`,
    EMPLOYEES_EXPORT : `${PageName.EMPLOYEES}:${PermissionAction.EXPORT}`,
    EMPLOYEES_REPORT : `${PageName.EMPLOYEES}:${PermissionAction.REPORT}`,
    EMPLOYEES_DASHBOARD : `${PageName.EMPLOYEES}:${PermissionAction.DASHBOARD}`,

    HR_SETTINGS_READ : `${PageName.HR_SETTINGS}:${PermissionAction.READ}`,
    HR_SETTINGS_CREATE : `${PageName.HR_SETTINGS}:${PermissionAction.CREATE}`,
    HR_SETTINGS_EDIT : `${PageName.HR_SETTINGS}:${PermissionAction.EDIT}`,
    HR_SETTINGS_DELETE : `${PageName.HR_SETTINGS}:${PermissionAction.DELETE}`,
    HR_SETTINGS_LOGS : `${PageName.HR_SETTINGS}:${PermissionAction.LOGS}`,
    ROTA_READ : `${PageName.ROTA}:${PermissionAction.READ}`,
    ROTA_EDIT : `${PageName.ROTA}:${PermissionAction.EDIT}`,
    ROTA_LOGS : `${PageName.ROTA}:${PermissionAction.LOGS}`,
    LEAVE_READ : `${PageName.LEAVE}:${PermissionAction.READ}`,
    LEAVE_CREATE : `${PageName.LEAVE}:${PermissionAction.CREATE}`,
    LEAVE_APPROVE : `${PageName.LEAVE}:${PermissionAction.APPROVE}`,
    LEAVE_LOGS : `${PageName.LEAVE}:${PermissionAction.LOGS}`,
    ATTENDANCE_READ : `${PageName.ATTENDANCE}:${PermissionAction.READ}`,
    ATTENDANCE_EDIT : `${PageName.ATTENDANCE}:${PermissionAction.EDIT}`,
    ATTENDANCE_LOGS : `${PageName.ATTENDANCE}:${PermissionAction.LOGS}`,
    EXPENSE_CLAIMS_READ : `${PageName.EXPENSE_CLAIMS}:${PermissionAction.READ}`,
    EXPENSE_CLAIMS_APPROVE : `${PageName.EXPENSE_CLAIMS}:${PermissionAction.APPROVE}`,
    EXPENSE_CLAIMS_FINANCE : `${PageName.EXPENSE_CLAIMS}:${PermissionAction.FINANCE}`,
    EXPENSE_CLAIMS_LOGS : `${PageName.EXPENSE_CLAIMS}:${PermissionAction.LOGS}`,
    EXPENSE_CLAIMS_EXPORT : `${PageName.EXPENSE_CLAIMS}:${PermissionAction.EXPORT}`,
    PURCHASES_READ : `${PageName.PURCHASES}:${PermissionAction.READ}`,
    PURCHASES_CREATE : `${PageName.PURCHASES}:${PermissionAction.CREATE}`,
    PURCHASES_EDIT : `${PageName.PURCHASES}:${PermissionAction.EDIT}`,
    PURCHASES_APPROVE : `${PageName.PURCHASES}:${PermissionAction.APPROVE}`,
    PURCHASES_FINANCE : `${PageName.PURCHASES}:${PermissionAction.FINANCE}`,
    PURCHASES_LOGS : `${PageName.PURCHASES}:${PermissionAction.LOGS}`,
    PAYROLL_READ : `${PageName.PAYROLL}:${PermissionAction.READ}`,
    PAYROLL_CREATE : `${PageName.PAYROLL}:${PermissionAction.CREATE}`,
    PAYROLL_EDIT : `${PageName.PAYROLL}:${PermissionAction.EDIT}`,
    PAYROLL_APPROVE : `${PageName.PAYROLL}:${PermissionAction.APPROVE}`,
    PAYROLL_FINANCE : `${PageName.PAYROLL}:${PermissionAction.FINANCE}`,
    PAYROLL_LOGS : `${PageName.PAYROLL}:${PermissionAction.LOGS}`,
    PAYROLL_EXPORT : `${PageName.PAYROLL}:${PermissionAction.EXPORT}`,
    LIFECYCLE_READ : `${PageName.LIFECYCLE}:${PermissionAction.READ}`,
    LIFECYCLE_CREATE : `${PageName.LIFECYCLE}:${PermissionAction.CREATE}`,
    LIFECYCLE_EDIT : `${PageName.LIFECYCLE}:${PermissionAction.EDIT}`,
    LIFECYCLE_APPROVE : `${PageName.LIFECYCLE}:${PermissionAction.APPROVE}`,
    LIFECYCLE_LOGS : `${PageName.LIFECYCLE}:${PermissionAction.LOGS}`,
    LIFECYCLE_EXPORT : `${PageName.LIFECYCLE}:${PermissionAction.EXPORT}`,
    HR_CASES_READ : `${PageName.HR_CASES}:${PermissionAction.READ}`,
    HR_CASES_CREATE : `${PageName.HR_CASES}:${PermissionAction.CREATE}`,
    HR_CASES_EDIT : `${PageName.HR_CASES}:${PermissionAction.EDIT}`,
    HR_CASES_LOGS : `${PageName.HR_CASES}:${PermissionAction.LOGS}`,
    AI_SETTINGS_READ : `${PageName.AI_SETTINGS}:${PermissionAction.READ}`,
    AI_SETTINGS_EDIT : `${PageName.AI_SETTINGS}:${PermissionAction.EDIT}`,
    // Business development: document analyses, proposals and replies; settings = masking words, company rules, checklists.
    BUSINESS_DEVELOPMENT_READ : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.READ}`,
    BUSINESS_DEVELOPMENT_CREATE : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.CREATE}`,
    BUSINESS_DEVELOPMENT_EDIT : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.EDIT}`,
    BUSINESS_DEVELOPMENT_DELETE : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.DELETE}`,
    BUSINESS_DEVELOPMENT_RESTORE : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.RESTORE}`,
    BUSINESS_DEVELOPMENT_LOGS : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.LOGS}`,
    BUSINESS_DEVELOPMENT_EXPORT : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.EXPORT}`,
    BUSINESS_DEVELOPMENT_APPROVE : `${PageName.BUSINESS_DEVELOPMENT}:${PermissionAction.APPROVE}`,
    BD_SETTINGS_READ : `${PageName.BD_SETTINGS}:${PermissionAction.READ}`,
    BD_SETTINGS_EDIT : `${PageName.BD_SETTINGS}:${PermissionAction.EDIT}`,
    RESEARCH_READ : `${PageName.RESEARCH}:${PermissionAction.READ}`,
    RESEARCH_CREATE : `${PageName.RESEARCH}:${PermissionAction.CREATE}`,
    RESEARCH_EDIT : `${PageName.RESEARCH}:${PermissionAction.EDIT}`,
    RESEARCH_DELETE : `${PageName.RESEARCH}:${PermissionAction.DELETE}`,
    // Marketing: briefs and content items (approve = text and design sign-off); review = the weekly results; settings = brand voice, rules, facts, assignees.
    MARKETING_READ : `${PageName.MARKETING}:${PermissionAction.READ}`,
    MARKETING_CREATE : `${PageName.MARKETING}:${PermissionAction.CREATE}`,
    MARKETING_EDIT : `${PageName.MARKETING}:${PermissionAction.EDIT}`,
    MARKETING_DELETE : `${PageName.MARKETING}:${PermissionAction.DELETE}`,
    MARKETING_RESTORE : `${PageName.MARKETING}:${PermissionAction.RESTORE}`,
    MARKETING_LOGS : `${PageName.MARKETING}:${PermissionAction.LOGS}`,
    MARKETING_EXPORT : `${PageName.MARKETING}:${PermissionAction.EXPORT}`,
    MARKETING_APPROVE : `${PageName.MARKETING}:${PermissionAction.APPROVE}`,
    MARKETING_REVIEW_READ : `${PageName.MARKETING_REVIEW}:${PermissionAction.READ}`,
    MARKETING_REVIEW_EDIT : `${PageName.MARKETING_REVIEW}:${PermissionAction.EDIT}`,
    MARKETING_REVIEW_EXPORT : `${PageName.MARKETING_REVIEW}:${PermissionAction.EXPORT}`,
    MARKETING_SETTINGS_READ : `${PageName.MARKETING_SETTINGS}:${PermissionAction.READ}`,
    MARKETING_SETTINGS_EDIT : `${PageName.MARKETING_SETTINGS}:${PermissionAction.EDIT}`,
    MISSION_SETTINGS_READ : `${PageName.MISSION_SETTINGS}:${PermissionAction.READ}`,
    MISSION_SETTINGS_EDIT : `${PageName.MISSION_SETTINGS}:${PermissionAction.EDIT}`,
    // Training: jobs from request to payment (finance = prices, costs, margins, invoices and bills); settings = courses and rules.
    TRAINING_READ : `${PageName.TRAINING}:${PermissionAction.READ}`,
    TRAINING_CREATE : `${PageName.TRAINING}:${PermissionAction.CREATE}`,
    TRAINING_EDIT : `${PageName.TRAINING}:${PermissionAction.EDIT}`,
    TRAINING_DELETE : `${PageName.TRAINING}:${PermissionAction.DELETE}`,
    TRAINING_RESTORE : `${PageName.TRAINING}:${PermissionAction.RESTORE}`,
    TRAINING_LOGS : `${PageName.TRAINING}:${PermissionAction.LOGS}`,
    TRAINING_EXPORT : `${PageName.TRAINING}:${PermissionAction.EXPORT}`,
    TRAINING_FINANCE : `${PageName.TRAINING}:${PermissionAction.FINANCE}`,
    TRAINING_SETTINGS_READ : `${PageName.TRAINING_SETTINGS}:${PermissionAction.READ}`,
    TRAINING_SETTINGS_EDIT : `${PageName.TRAINING_SETTINGS}:${PermissionAction.EDIT}`,
}


export interface UserCallPermissions {
    userId:number;
    pages:Page[];

}